import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils-app";
import { toast } from "sonner";

/**
 * Admin-only reply form. Opens from a user_contact message in the admin's
 * inbox. The reply is delivered to the original sender's inbox as an
 * admin_reply message; the admin's own copy is marked read + flagged.
 */
export default function AdminReplyDialog({ open, onClose, message, onReplied }) {
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => setBody("");
  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const submit = async () => {
    if (!body.trim()) {
      toast.error("Reply cannot be empty");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/messages/${message.id}/reply`, { body: body.trim() });
      toast.success("Reply sent to user");
      reset();
      onReplied?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!message) return null;
  const meta = message.metadata || {};
  const subject = meta.subject || "user message";
  const fromName = meta.from_user_name || meta.from_user_email || "user";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-lg" data-testid="admin-reply-dialog">
        <DialogHeader>
          <DialogTitle>Reply to {fromName}</DialogTitle>
          <DialogDescription>
            Subject: <span className="font-semibold text-ink">{subject}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="bg-[#FAFAFA] border border-[#E5E5E5] rounded-lg p-3 text-sm whitespace-pre-wrap text-[#555] max-h-40 overflow-y-auto">
          {message.body}
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
            Your reply *
          </Label>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={6}
            maxLength={4000}
            placeholder="Write your response…"
            className="mt-1 rounded-lg"
            data-testid="admin-reply-body"
          />
          <p className="mt-1 text-[11px] text-[#6B6B6B] text-right">
            {body.length}/4000
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} className="rounded-full">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
            data-testid="admin-reply-submit"
          >
            {submitting ? "Sending…" : "Send reply"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
